/**
 * The music bed: two loops, one gain path, and no relationship to the cues.
 *
 * ### Music is not a cue, and it must not go through the voice limiter
 *
 * `VoiceLimiter` exists so that twelve tanks firing at once do not sum past
 * full scale — it counts overlapping *events*, drops the newest, and hands back
 * a gain that falls as the stack deepens. Every one of those properties is
 * wrong for a music bed, and in both directions:
 *
 * - **The bed would be starved.** It is one continuous source that never ends,
 *   so it can never free the slot it took. Asking the limiter for it means the
 *   music holds a twelfth of the arena's audio budget for the whole match and is
 *   then attenuated by whatever else is sounding — so the music would duck for
 *   every shot, which is a mix decision nobody took.
 * - **The cues would be starved.** A voice that never ends is a voice the cap
 *   can never reclaim. `MAX_TRACK_LOOPS` exists for exactly this reason one
 *   asset along: the track rumbles were given their own budget *because* they
 *   never end. Music is the same argument with a budget of one.
 *
 * So the bed has its own `GainNode` straight to the destination and is
 * accounted for nowhere in `policy.ts`. `__sowAudio().music.outsideLimiter`
 * states it as a fact the e2e can read, rather than as a promise in a comment.
 *
 * ### It is also outside the cue compressor, deliberately
 *
 * `GameAudio`'s `DynamicsCompressorNode` is there to catch the *sum of a busy
 * firefight*. Feeding a continuous bed into it would keep it engaged from the
 * first frame to the last, so every cue would arrive into a compressor that is
 * already working and the headroom the cues were tuned against would be gone.
 * The bed is quiet and pre-normalised (see `docs/audio.md`), so it needs no
 * catching of its own.
 *
 * ### Decisions and graph in one file
 *
 * The rule elsewhere is that decisions live in `policy.ts` and node graphs in
 * `synth.ts` / `samples.ts`. Music is one exception and it is the honest one:
 * putting music's numbers in `policy.ts` would put music inside the file the cue
 * catalogue and the limiter live in, which is precisely the coupling this
 * feature must not have. The pure half is exported and unit-tested in
 * `music.test.ts`; nothing below `musicTrackFor` decides anything.
 *
 * ### Nothing here may be smoothed against frame time
 *
 * Same invariant as `synth.ts` and `samples.ts`. Every gain move is
 * `setTargetAtTime` on the audio clock, so a crossfade takes the same
 * wall-clock time at 4 fps under SwiftShader as it does at 144 fps.
 */
import { audioMuted, masterVolume, musicMuted, musicVolume } from '@/state/settings';

// ─── Decisions ───────────────────────────────────────────────────────────────

/**
 * Two tracks, and the count is a real decision rather than a starting point.
 *
 * One bed everywhere would be cheapest and is wrong: the garage is a place you
 * stand and read numbers in, and the arena is a place you are being shot at in,
 * and a single piece of music can serve one of those well. Three or more would
 * buy a third mood at the cost of a third of a megabyte and a third loop to
 * keep seamless — and there is no third *place*. The loading screen is not one:
 * it is the transition between the two, it is up for between half a second and
 * twenty, and giving it its own track would mean two crossfades in the time it
 * takes to read the map name. It keeps the garage bed instead, so there is
 * nothing to fetch and nothing to cut on the way into a battle.
 */
export type MusicTrackId = 'garage' | 'battle';

export const MUSIC_TRACK_IDS: readonly MusicTrackId[] = ['garage', 'battle'];

/**
 * Which bed a screen gets.
 *
 * `null` is a real answer and means *no music at all* — nothing currently asks
 * for it, and it is what makes the bed disposable without a special case.
 */
export type MusicScene = 'garage' | 'battle' | 'none';

export function musicTrackFor(scene: MusicScene): MusicTrackId | null {
  if (scene === 'garage') return 'garage';
  if (scene === 'battle') return 'battle';
  return null;
}

export const MUSIC_URLS: Record<MusicTrackId, string> = {
  garage: '/assets/audio/music-garage.webm',
  battle: '/assets/audio/music-battle.webm',
};

/**
 * How long each loop is, in seconds.
 *
 * Stated here for the same reason `SAMPLE_SECONDS` is: it is a fact about the
 * asset that the decisions depend on, and a file that has been re-cut should
 * show up as a number on the dev probe rather than as a bed that stumbles every
 * pass. `MusicBed` checks the decoded length against these and reports any
 * disagreement.
 */
export const MUSIC_SECONDS: Record<MusicTrackId, number> = {
  garage: 34,
  battle: 28,
};

/** Same tolerance, and the same reasoning, as `samples.ts`. */
export const MUSIC_LENGTH_TOLERANCE_SECONDS = 0.05;

/**
 * The crossfade time constant, in seconds, for `setTargetAtTime`.
 *
 * A time constant rather than a duration because that is the only kind of ease
 * that cannot be frame-rate dependent — Web Audio runs it on the audio clock.
 * A track swap happens once per deploy and once per return to the garage, and
 * it lands in the middle of the loading screen, which is the best possible
 * moment for it: nothing is being aimed and nothing is being read.
 */
export const MUSIC_GLIDE = 0.9;

/**
 * How many time constants to wait before stopping a faded-out source.
 *
 * `setTargetAtTime` is asymptotic and never actually reaches its target, so a
 * source stopped at one time constant is stopped at 37% of its level, which is
 * a click. Five puts it under 1%.
 */
export const MUSIC_GLIDE_TAIL = 5;

/**
 * The gain the music path should carry.
 *
 * Pure, and the single answer to "how loud is the music" — the same discipline
 * `currentMasterVolume` follows, one bus along. Two mutes reach it and they mean
 * different things: `audioMuted` is *the game makes no noise*, `musicMuted` is
 * *keep the guns, lose the music*. Either one silences the bed.
 */
export function musicGain(
  master: number,
  music: number,
  masterIsMuted: boolean,
  musicIsMuted: boolean,
): number {
  if (masterIsMuted || musicIsMuted) return 0;
  if (!Number.isFinite(master) || !Number.isFinite(music)) return 0;
  return Math.max(0, Math.min(1, master)) * Math.max(0, Math.min(1, music));
}

/**
 * Whether the bed should be *running* at all, as opposed to merely quiet.
 *
 * A muted bed is torn down rather than played at zero, and that is deliberate:
 * mute means off. A player who mutes pays no download, no decode and no
 * looping source — which is the whole point of a mute on a machine slow enough
 * to want one. It is gated on the two mutes and **not** on the volume value, so
 * a slider parked at zero does not restart the bed from the top every time it
 * is nudged.
 */
export function musicShouldPlay(masterIsMuted: boolean, musicIsMuted: boolean): boolean {
  return !masterIsMuted && !musicIsMuted;
}

/** Reads the live settings. Not reactive — callers re-read on change. */
export function currentMusicGain(): number {
  return musicGain(masterVolume.value, musicVolume.value, audioMuted.value, musicMuted.value);
}

/** Reads the live settings. */
export function currentMusicShouldPlay(): boolean {
  return musicShouldPlay(audioMuted.value, musicMuted.value);
}

// ─── The mute key ────────────────────────────────────────────────────────────

/**
 * The key that mutes the music, as a `KeyboardEvent.code`.
 *
 * **Checked against every binding in *Controls*.** `input.ts` claims `KeyW`,
 * `KeyS`, `KeyA`, `KeyD`, the four arrows, `Space`, `Digit1`–`Digit6` and
 * `Numpad1`–`Numpad6`; `GameHud.vue` claims `Escape`; the mouse buttons are the
 * trigger and free look. `KeyM` collides with none of them, it is the letter of
 * the word, and it is far enough from `WASD` that it cannot be hit while
 * driving.
 */
export const MUSIC_MUTE_CODE = 'KeyM';

/** What the control tells the player to press. */
export const MUSIC_MUTE_LABEL = 'M';

function typingInto(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Attaches the mute key. Returns a detach function.
 *
 * **Deliberately not in `game/input.ts`**, and not because that file is
 * somebody else's — because it is the wrong place. `InputState` is the *arena's*
 * input source: it is constructed with the scene, torn down with it, and
 * suspends itself while the escape menu is up. A mute has to work on the sign-in
 * form, in the garage, on the loading screen and mid-firefight, so a binding
 * that only exists while a match is running is a binding that is missing
 * exactly when somebody walks into the room. It lives beside the thing it
 * controls instead, installed once from `initGameAudio()`.
 *
 * Three guards, each removing a way for this to be a nuisance:
 *
 * - **Modifiers are ignored**, so `Cmd+M` still minimises the window and
 *   `Ctrl+M` is still whatever the browser thinks it is.
 * - **Typing is ignored**, or the sign-in form could not accept a nickname with
 *   an `m` in it without silencing the game.
 * - **`preventDefault` is never called and the listener is not capturing**, so
 *   this cannot swallow a key anything else wanted. It is additive by
 *   construction.
 *
 * `code` is the primary test because that is what `input.ts` matches on, so the
 * two agree about what a physical key is; `key` is accepted as well, which is
 * what makes the binding reachable on a layout where `KeyM` is not under the
 * letter M.
 */
export function installMusicMuteKey(): () => void {
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
    const hit = event.code === MUSIC_MUTE_CODE || event.key.toLowerCase() === 'm';
    if (!hit) return;
    if (typingInto(event.target)) return;
    musicMuted.value = !musicMuted.value;
  };
  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}

// ─── The graph ───────────────────────────────────────────────────────────────

interface BedVoice {
  id: MusicTrackId;
  source: AudioBufferSourceNode;
  gain: GainNode;
}

/**
 * One looping bed at a time, on its own gain path.
 *
 * ### Everything here fails to silence, and nothing here logs
 *
 * A 404, a `decodeAudioData` that rejects, a codec the browser does not carry, a
 * fetch a policy refused: each ends with the track recorded as failed and the
 * game exactly as playable as before. That mirrors the *discipline* the cues
 * follow — no throw, no console noise, no broken graph, and a counter on
 * `__sowAudio()` so the failure is answerable — but not their *mechanism*, and
 * the difference is worth stating. A recorded cue keeps a synthesised fallback
 * because the game must stay **audible**: a gun that makes no noise is a gun
 * whose feedback has gone. Music is atmosphere, not feedback, so the correct
 * degradation is silence, and inventing a synthesised stand-in would add a
 * permanent oscillator graph to cover a file that is either there or is not.
 * `fellSilent` counts it, so silence-because-missing and silence-because-muted
 * are two different numbers rather than one shrug.
 */
export class MusicBed {
  private readonly buffers = new Map<MusicTrackId, AudioBuffer>();
  private readonly failures = new Map<MusicTrackId, string>();
  private readonly inFlight = new Set<MusicTrackId>();
  private readonly mismatched = new Map<MusicTrackId, number>();

  private voice: BedVoice | null = null;
  private wanted: MusicTrackId | null = null;
  private disposed = false;

  /** Dev counters. The only way to answer any of this without listening. */
  private startedCount = 0;
  private switchCount = 0;
  /** Times a start was refused because the context was not running. */
  private blockedByAutoplay = 0;
  /** Tracks that were wanted and could not be had. */
  private readonly silent = new Set<MusicTrackId>();

  constructor(
    private readonly ctx: AudioContext,
    /** The bed's own gain node — never the cue bus, never the compressor. */
    private readonly bus: GainNode,
  ) {}

  /** Which track is wanted, or `null` for none. Idempotent. */
  select(scene: MusicScene): void {
    if (this.disposed) return;
    this.wanted = musicTrackFor(scene);
    this.refresh();
  }

  /**
   * Reconciles the graph with what is wanted, the settings and the context
   * state.
   *
   * Called on a scene change, on the mute or volume changing, and when the
   * context finally unlocks — which is the whole autoplay story: the bed is
   * *asked for* whenever the app likes and starts on the first frame it is
   * actually allowed to, with no caller having to know whether a gesture has
   * happened yet.
   */
  refresh(): void {
    if (this.disposed) return;
    const wanted = this.wanted;
    const allowed = currentMusicShouldPlay();

    if (!wanted || !allowed) {
      this.stopVoice();
      return;
    }

    // Nothing is fetched for a muted bed, so this is also the point at which an
    // unmute triggers the download. `decodeAudioData` is happy on a suspended
    // context, so the buffer can be warm before the gesture arrives.
    void this.load(wanted);

    const buffer = this.buffers.get(wanted);
    if (!buffer) return;

    if (this.ctx.state !== 'running') {
      // Autoplay said no, or the player has not clicked anything yet. Counted,
      // not logged: `refresh()` runs again from `contextUnlocked()`.
      this.blockedByAutoplay += 1;
      return;
    }

    if (this.voice?.id === wanted) return;
    if (this.voice) this.switchCount += 1;
    this.stopVoice();
    this.voice = this.build(wanted, buffer);
    this.startedCount += 1;
    this.applyGain();
  }

  /** The context reached `running`. Start whatever was wanted all along. */
  contextUnlocked(): void {
    this.refresh();
  }

  /** The volume or a mute moved. */
  applyGain(): void {
    if (this.disposed) return;
    const wanted = currentMusicGain();
    // Ramped rather than assigned, for the same reason the master bus is:
    // a step change in gain while sound is passing through is a click, and
    // dragging a slider would produce one per pixel.
    this.bus.gain.setTargetAtTime(wanted, this.ctx.currentTime, 0.02);
    // The ramp is asymptotic and a test — reasonably — asks whether mute really
    // put zero on the bus, so land the value exactly a moment later too. Same
    // trick, and the same reason, as `GameAudio.applyVolume`.
    this.bus.gain.setValueAtTime(wanted, this.ctx.currentTime + 0.1);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stopVoice();
  }

  /** Everything the dev probe reports about the bed. */
  report(): {
    ids: MusicTrackId[];
    wanted: MusicTrackId | null;
    playing: MusicTrackId | null;
    loaded: number;
    failed: number;
    pending: number;
    buffers: Record<string, boolean>;
    failures: Record<string, string>;
    lengthMismatches: Record<string, number>;
    started: number;
    switched: number;
    blockedByAutoplay: number;
    fellSilent: number;
    silentTracks: MusicTrackId[];
    outsideLimiter: true;
  } {
    return {
      ids: [...MUSIC_TRACK_IDS],
      wanted: this.wanted,
      playing: this.voice?.id ?? null,
      loaded: this.buffers.size,
      failed: this.failures.size,
      pending: this.inFlight.size,
      buffers: Object.fromEntries(MUSIC_TRACK_IDS.map((id) => [id, this.buffers.has(id)])),
      failures: Object.fromEntries(this.failures),
      lengthMismatches: Object.fromEntries(this.mismatched),
      started: this.startedCount,
      switched: this.switchCount,
      blockedByAutoplay: this.blockedByAutoplay,
      fellSilent: this.silent.size,
      silentTracks: [...this.silent],
      // Stated as data rather than as a comment, so the e2e can assert that the
      // bed is on its own path instead of trusting that it still is.
      outsideLimiter: true,
    };
  }

  private async load(id: MusicTrackId): Promise<void> {
    if (this.buffers.has(id) || this.inFlight.has(id) || this.failures.has(id)) return;
    this.inFlight.add(id);
    try {
      const response = await fetch(MUSIC_URLS[id]);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const bytes = await response.arrayBuffer();
      const buffer = await decodeMusic(this.ctx, bytes);
      this.buffers.set(id, buffer);
      const expected = MUSIC_SECONDS[id];
      if (Math.abs(buffer.duration - expected) > MUSIC_LENGTH_TOLERANCE_SECONDS) {
        this.mismatched.set(id, buffer.duration);
      }
    } catch (err) {
      // Every failure lands here and nowhere else. The bed stays silent and the
      // game is unchanged; nothing is logged and nothing throws.
      this.failures.set(id, err instanceof Error ? err.message : 'failed');
      this.silent.add(id);
    } finally {
      this.inFlight.delete(id);
      // A load that finished while the scene was already chosen is the ordinary
      // case, not a race to be avoided: the bed starts on the frame the buffer
      // lands rather than on the frame it was asked for.
      if (!this.disposed) this.refresh();
    }
  }

  private build(id: MusicTrackId, buffer: AudioBuffer): BedVoice {
    const ctx = this.ctx;
    const now = ctx.currentTime;

    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.gain.setTargetAtTime(1, now, MUSIC_GLIDE);
    gain.connect(this.bus);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    // The loop is seamless by construction — its own head is crossfaded over
    // its tail, exactly as `move-loop.webm` is — so this is genuinely gapless
    // and there is nothing to hide with a crossfade here. Opus rather than MP3
    // is what makes that true: an MP3 decodes with encoder delay and padding
    // welded to both ends and cannot loop without a gap however well it was cut.
    source.loop = true;
    source.connect(gain);
    // From the start of the loop rather than from a random offset, which is the
    // opposite of what the track rumbles do and for the opposite reason: six
    // rumbles at offset zero are one phase-locked drone, while one piece of
    // music should begin where it was written to begin.
    source.start(now);

    return { id, source, gain };
  }

  private stopVoice(): void {
    const voice = this.voice;
    if (!voice) return;
    this.voice = null;
    const now = this.ctx.currentTime;
    voice.gain.gain.cancelScheduledValues(now);
    voice.gain.gain.setTargetAtTime(0, now, MUSIC_GLIDE);
    const stopAt = now + MUSIC_GLIDE * MUSIC_GLIDE_TAIL;
    try {
      voice.source.stop(stopAt);
    } catch {
      // Already stopped. Nothing to do and nothing worth saying.
    }
    voice.source.onended = () => {
      voice.gain.disconnect();
    };
  }
}

/**
 * `decodeAudioData` has two signatures and browsers disagree about which.
 *
 * The same four lines as `samples.ts`, and deliberately not shared with it: that
 * file is the recorded *cue* path and this one must be able to be deleted
 * without touching it. Four lines is a cheaper coupling to avoid than to have.
 */
function decodeMusic(ctx: BaseAudioContext, bytes: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise<AudioBuffer>((resolve, reject) => {
    const maybe = ctx.decodeAudioData(bytes, resolve, reject);
    if (maybe && typeof maybe.then === 'function') maybe.then(resolve, reject);
  });
}
